/**
 * Service layer for workspace custom agents.
 *
 * A custom agent is one of the template's built-in behaviors with a workspace
 * administrator's own name, words and documents (SPEC-custom-agent-template.md
 * §2 J; migration 917). This file reads and writes its identity half —
 * workspace_custom_agents. The common half (enabled, model, prompt overlay,
 * evidence sources, documents, triggers, review, the behavior's own switches)
 * is its agent_settings row under `customAgentType(id)`, read and written
 * through agentSettingsService like every other agent.
 *
 * Writes go through upsert_custom_agent / delete_custom_agent only; both check
 * that the caller administers the workspace.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  definitionForCustomAgent,
  isAgentBehaviorKind,
  type AgentBehaviorKind,
  type AgentDefinition,
  type CustomAgentIdentity,
} from './ai/agentDefinitions';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CustomAgent extends CustomAgentIdentity {
  workspaceId: string;
  slug: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCustomAgentInput {
  /** Absent to make a new agent. */
  id?: string | null;
  workspaceId: string;
  name: string;
  description?: string | null;
  /** Fixed once the agent is made; the server refuses a change. */
  behaviorKind: AgentBehaviorKind;
  /** The model the new agent starts with (its behavior's default). Ignored on rename. */
  model?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function toCustomAgentSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Rows whose behavior this build does not know are left out rather than guessed at. */
function mapRow(row: Record<string, unknown>): CustomAgent | null {
  const kind = String(row.behavior_kind ?? '');
  if (!isAgentBehaviorKind(kind)) return null;
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    slug: row.slug as string,
    description: (row.description as string | null) ?? null,
    behaviorKind: kind,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function customAgentDefinition(agent: CustomAgent): AgentDefinition {
  return definitionForCustomAgent(agent);
}

// ── API ───────────────────────────────────────────────────────────────────────

export async function listCustomAgents(
  workspaceId: string,
): Promise<{ data: CustomAgent[] | null; error: string | null }> {
  const { data, error } = await getSupabaseClient()
    .from('workspace_custom_agents')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: true });

  if (error) return { data: null, error: error.message };
  const rows = (data ?? []) as Record<string, unknown>[];
  return { data: rows.map(mapRow).filter((a): a is CustomAgent => a !== null), error: null };
}

export async function getCustomAgent(
  id: string,
): Promise<{ data: CustomAgent | null; error: string | null }> {
  const { data, error } = await getSupabaseClient()
    .from('workspace_custom_agents')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) return { data: null, error: error.message };
  if (!data) return { data: null, error: 'That agent does not exist.' };
  return { data: mapRow(data as Record<string, unknown>), error: null };
}

export async function upsertCustomAgent(
  input: UpsertCustomAgentInput,
): Promise<{ data: CustomAgent | null; error: string | null }> {
  const { data, error } = await getSupabaseClient().rpc('upsert_custom_agent', {
    p_id: input.id ?? null,
    p_workspace_id: input.workspaceId,
    p_name: input.name,
    p_slug: toCustomAgentSlug(input.name),
    p_description: input.description ?? null,
    p_behavior_kind: input.behaviorKind,
    p_model: input.model ?? null,
  });

  if (error) return { data: null, error: error.message };
  return { data: mapRow(data as Record<string, unknown>), error: null };
}

export async function deleteCustomAgent(
  id: string,
  workspaceId: string,
): Promise<{ error: string | null }> {
  const { error } = await getSupabaseClient().rpc('delete_custom_agent', {
    p_id: id,
    p_workspace_id: workspaceId,
  });

  if (error) return { error: error.message };
  return { error: null };
}
